import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Brain, FileCheck, Send, Moon, CheckCircle2, ArrowRight, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import LoginModal from '../components/LoginModal';
import SEO, { articleSchema } from '../components/SEO';
import { SERVICE_DISCLAIMER } from '../data/plans';
import './LegalPage.css';
import './AiJobFinderPage.css';

const AiJobFinderPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoginOpen, setIsLoginOpen] = useState(false);

  return (
    <div className="legal-page ai-job">
      <SEO
        title="Yapay Zeka İş Bulsun"
        description="Yapay zeka iş bulsun: yapay zeka ile iş ara, ücretsiz CV oluştur, profiline en uygun ilanları yapay zeka sizin için tarasın ve eşleştirsin. isBulsun.com ile iş aramayı yapay zekaya bırakın."
        keywords="yapay zeka iş bulsun, yapay zeka ile iş ara, yapay zeka ile cv oluştur, yapay zeka iş arama, ai ile iş bul"
        path="/yapay-zeka-is-bulsun"
        structuredData={articleSchema({
          title: 'Yapay Zeka İş Bulsun',
          description: 'Yapay zeka ile iş aramanın, CV oluşturmanın ve ilan eşleştirmenin nasıl çalıştığını anlatan rehber.',
          path: '/yapay-zeka-is-bulsun',
          datePublished: '2026-09-28',
        })}
      />

      <div className="ai-job__hero">
        <p className="ai-job__eyebrow">İş aramak yerine, işi yapay zeka arasın</p>
        <h1 className="ai-job__title">Yapay Zeka İş Bulsun</h1>
        <p className="ai-job__lead">
          Onlarca ilan sitesinde tek tek arama yapmak, her pozisyon için CV'yi yeniden
          düzenlemek, aynı formu defalarca doldurmak... Klasik iş arama süreci yorucu ve zaman
          alıcı. isBulsun.com'da bu işi yapay zekaya bırakırsınız: siz profilinizi oluşturursunuz,
          yapay zeka sizin için ilanları tarar, uygun olanları eşleştirir ve başvuru formu
          taslağını hazırlar; gönderim her zaman sizin onayınızla olur.
        </p>
      </div>

      <div className="legal-page__container ai-job__body">
        <h2>Neden "Yapay Zeka İş Bulsun" Diyoruz?</h2>
        <p>
          Bir iş ilanına uygun olup olmadığınızı anlamak için ilanı okumanız, kendi deneyiminizle
          karşılaştırmanız ve CV'nizi o pozisyona göre uyarlamanız gerekir. Bunu onlarca ilan için
          tekrar tekrar yapmak, tam zamanlı bir iş kadar yorucu olabilir. Yapay zeka bu analiz
          işini sizin adınıza, siz uyurken bile arka planda yapabilir: profilinizle ilanları
          karşılaştırır, uyum skorunu hesaplar ve size gerçekten uygun olanları öne çıkarır.
        </p>

        <h2>Yapay Zeka İş Ararken Tam Olarak Ne Yapar?</h2>
        <ul>
          <li><strong>CV'nizi analiz eder:</strong> Mevcut CV'nizi yükleyin, yapay zeka eksik veya zayıf kalan bölümleri size gösterir.</li>
          <li><strong>İlanları tarar:</strong> Profilinize uygun yeni ilanlar yayınlandıkça yapay zeka bunları yakalar.</li>
          <li><strong>Uyum skoru hesaplar:</strong> Her ilan için ne kadar uygun olduğunuzu, eksik anahtar kelimeleri ve ATS uyumluluğunu görürsünüz.</li>
          <li><strong>Form taslağını hazırlar:</strong> Onay verdiğiniz ilanlar için başvuru formu taslağı hazır tutulur; gönderim yalnızca sizin onayınızla gerçekleşir.</li>
        </ul>

        <h2>Yapay Zeka ile CV Oluşturmak Neden Daha Hızlı?</h2>
        <p>
          Sıfırdan CV yazmak yerine, sohbet eder gibi sorulara cevap vererek dakikalar içinde
          profesyonel, ATS uyumlu bir CV oluşturabilirsiniz. Elinizde zaten bir CV varsa, onu
          yükleyin, yapay zeka bilgilerinizi otomatik olarak ayıklayıp doldurur; siz yalnızca
          eksik kalan kısımları tamamlarsınız.
        </p>

        <h2>Yapay Zeka ile İş Ararken Dikkat Edilmesi Gerekenler</h2>
        <p>
          Yapay zeka aramayı ve eşleştirmeyi hızlandırır, ama son kararı hâlâ siz verirsiniz. En
          iyi sonuç için:
        </p>
        <ul>
          <li>Profilinizi ve deneyim bilgilerinizi güncel tutun; eşleştirme kalitesi verdiğiniz bilgiyle doğru orantılıdır.</li>
          <li>Gönderilecek her form taslağını göndermeden önce gözden geçirin; onay hâlâ sizde.</li>
          <li>Uyum skoru yüksek ilanlara öncelik verin, ama düşük skorlu ilanları da tamamen göz ardı etmeyin.</li>
        </ul>

        <p className="ai-job__disclaimer">{SERVICE_DISCLAIMER}</p>
      </div>

      <div className="ai-job__cta">
        <div className="ai-job__cta-icon"><Moon size={22} /></div>
        <h2 className="ai-job__cta-title">Siz Uyurken Yapay Zeka Sizin İçin İş Arasın İstemez misiniz?</h2>
        <p className="ai-job__cta-desc">
          Profilinizi bir kez oluşturun, gerisini yapay zekaya bırakın. Uygun bir ilan
          bulunduğunda haberdar olursunuz, üstelik tamamen ücretsiz başlayabilirsiniz.
        </p>
        <div className="ai-job__cta-actions">
          {user ? (
            <button className="ai-job__cta-btn ai-job__cta-btn--primary" onClick={() => navigate('/panel')}>
              <ArrowRight size={16} />
              Kariyerime Git
            </button>
          ) : (
            <>
              <button className="ai-job__cta-btn ai-job__cta-btn--primary" onClick={() => navigate('/')}>
                <Search size={16} />
                Hemen Arat
              </button>
              <button className="ai-job__cta-btn ai-job__cta-btn--secondary" onClick={() => setIsLoginOpen(true)}>
                Ücretsiz Üye Ol
                <ArrowRight size={16} />
              </button>
            </>
          )}
        </div>
        <div className="ai-job__cta-features">
          <span><CheckCircle2 size={14} /> Ücretsiz CV oluştur</span>
          <span><Brain size={14} /> Akıllı ilan eşleştirme</span>
          <span><FileCheck size={14} /> Uyum analizi</span>
          <span><Send size={14} /> Form taslağı hazırlama</span>
          <span><Sparkles size={14} /> Onay her zaman sizde</span>
        </div>
      </div>

      <LoginModal isOpen={isLoginOpen} onClose={() => setIsLoginOpen(false)} />
    </div>
  );
};

export default AiJobFinderPage;
